import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { Row, Col, Card, Tooltip, Spin, Avatar, Button, message, Modal, Select, Checkbox, InputNumber, Input, Drawer, List, Tag, Pagination, Progress, Alert, Radio, Collapse } from 'antd';
import { ScissorOutlined, FolderAddOutlined, QuestionCircleOutlined, SearchOutlined, UnorderedListOutlined, EyeOutlined, LeftOutlined, RightOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, MergeCellsOutlined, VideoCameraOutlined, PlayCircleOutlined, FilterOutlined, DownloadOutlined, CloudDownloadOutlined, LinkOutlined, FolderOpenOutlined, ClockCircleOutlined, CaretRightOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { Context } from '../context';
import { TableProject } from './TableProject';
import { Head } from './Head';
import { TableSettingsLangs } from './TableSettingsLangs';
import { Flag, getLanguageName } from '../utils';

const LANGUAGES = [
    { value: 'en', label: 'English (en)' },
    { value: 'ru', label: 'Russian (ru)' },
    { value: 'es', label: 'Spanish (es)' },
    { value: 'de', label: 'German (de)' },
    { value: 'kz', label: 'Kazakh (kz)' },
    { value: 'zh', label: 'Chinese (zh)' },
    { value: 'ar', label: 'Arabic (ar)' },
];

const TRANSLATION_MODELS = [
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (default)' },
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
    { value: 'gpt-4', label: 'GPT-4' },
];

const TASK_NAMES = {
    folder_create_translate_and_synt_audio: 'Создать перевод и озвучку',
    folder_merge_wavs_and_video: 'Склеить аудио и видео',
    cut_without_pauses: 'Вырезать паузы',
    create_max_without_pauses_video: 'Создать финальное видео',
    build_ru_for_media: 'Создать HLS (RU)',
    build_full_without_pauses_for_media: 'Создать HLS (No Pauses)',
    upload_zip_and_split: 'Разделить видео',
    split_existing: 'Разделить видео',
    upload_video_and_split: 'Разделить видео',
    upload_to_cloud: 'Загрузка в облако',
};

export function ProjectPage({ onLogout }) {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { fetchProjectFullData, splitVideo, createFolderTranslateAndSynt, getTaskStatus, folderMergeWavsAndVideo, cutWithoutPauses, createMaxWithoutPausesVideo, getDownloadLinks, getFolderLastModified, getLectureDuration, deleteProject, getProjectClips, resyncProjectFromDisk, state } = useContext(Context);
    const { langsShown, setLangsShown } = state;

    // Получаем роль текущего пользователя из localStorage
    const getCurrentUserRole = () => {
        try {
            const authUser = localStorage.getItem('auth_user');
            if (authUser) {
                const user = JSON.parse(authUser);
                return user.role || 1;
            }
        } catch (error) {
            console.error('Error getting user role:', error);
        }
        return 1; // По умолчанию обычный пользователь
    };
    const userRole = getCurrentUserRole();
    const isAdminOrSuperAdmin = userRole === 2 || userRole === 3;

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [splitLoading, setSplitLoading] = useState(false);
    const [createFolderLoading, setCreateFolderLoading] = useState(false);
    const [splitCompleted, setSplitCompleted] = useState(false);

    // Task status state
    const [taskStatuses, setTaskStatuses] = useState({});
    const pollingIntervalRef = useRef(null);
    const fetchTaskStatusesRef = useRef(null);
    const [selectedTaskForDetails, setSelectedTaskForDetails] = useState(null);
    const [taskStatusPanelKeys, setTaskStatusPanelKeys] = useState([]);  // Empty = collapsed by default

    // Modal state
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedFolders, setSelectedFolders] = useState([]);
    const [targetLang, setTargetLang] = useState('en');
    const [sourceLang, setSourceLang] = useState('ru');
    const [translationModel, setTranslationModel] = useState('claude-sonnet-4-6');
    const [createSyntAudio, setCreateSyntAudio] = useState(true);
    const [speedSyntAudio, setSpeedSyntAudio] = useState(1);
    const [reCreationTranslate, setReCreationTranslate] = useState(true);
    const [reCreationSyntAudio, setReCreationSyntAudio] = useState(true);

    // Range inputs
    const [rangeFrom, setRangeFrom] = useState(1);
    const [rangeTo, setRangeTo] = useState(1);

    // Merge Wavs and Video modal state
    const [mergeModalVisible, setMergeModalVisible] = useState(false);
    const [mergeLoading, setMergeLoading] = useState(false);
    const [mergeFolders, setMergeFolders] = useState([]);
    const [mergeLang, setMergeLang] = useState('en');
    const [createMergeMp4WavFolder, setCreateMergeMp4WavFolder] = useState(false);
    const [mergeRangeFrom, setMergeRangeFrom] = useState(1);
    const [mergeRangeTo, setMergeRangeTo] = useState(1);

    // Cut Without Pauses modal state
    const [cutModalVisible, setCutModalVisible] = useState(false);
    const [cutLoading, setCutLoading] = useState(false);
    const [cutFolderNum, setCutFolderNum] = useState(1);
    const [cutLang, setCutLang] = useState('en');

    // Create Max Without Pauses Video modal state
    const [maxVideoModalVisible, setMaxVideoModalVisible] = useState(false);
    const [maxVideoLoading, setMaxVideoLoading] = useState(false);
    const [maxVideoLang, setMaxVideoLang] = useState('en');
    const [doValidateCompleteness, setDoValidateCompleteness] = useState(false);
    const [forceRecreate, setForceRecreate] = useState(false);
    const [endFolder, setEndFolder] = useState(null);

    // Parts navigation state
    const [partsDrawerVisible, setPartsDrawerVisible] = useState(false);
    const [partsSearchQuery, setPartsSearchQuery] = useState('');
    const [selectedPartForPreview, setSelectedPartForPreview] = useState(null);
    const [partPreviewVisible, setPartPreviewVisible] = useState(false);
    const [currentPartPage, setCurrentPartPage] = useState(1);
    const [currentViewPartId, setCurrentViewPartId] = useState(null); // Part shown inline
    const [completionFilter, setCompletionFilter] = useState('all'); // 'all', 'done', 'in_progress', 'pending', 'partial'
    const partsPerPage = 20;
    const partRefs = useRef({});

    // Download links state
    const [downloadDrawerVisible, setDownloadDrawerVisible] = useState(false);
    const [downloadLinks, setDownloadLinks] = useState(null);
    const [downloadLinksLoading, setDownloadLinksLoading] = useState(false);
    const [downloadLangFilter, setDownloadLangFilter] = useState(null);
    const [downloadFolderFilter, setDownloadFolderFilter] = useState(null); // null = all, or specific folder number
    const [downloadFolderRangeFrom, setDownloadFolderRangeFrom] = useState(null);
    const [downloadFolderRangeTo, setDownloadFolderRangeTo] = useState(null);

    // Folder Last Modified state
    const [folderModifiedDrawerVisible, setFolderModifiedDrawerVisible] = useState(false);
    const [folderModifiedData, setFolderModifiedData] = useState(null);
    const [folderModifiedLoading, setFolderModifiedLoading] = useState(false);

    // Clips (cut-videos) state
    const [clipsDrawerVisible, setClipsDrawerVisible] = useState(false);
    const [clipsData, setClipsData] = useState(null);
    const [clipsLoading, setClipsLoading] = useState(false);
    const [resyncLoading, setResyncLoading] = useState(false);

    // Lecture Duration state
    const [lectureDuration, setLectureDuration] = useState(null);
    const [folderModifiedLangFilter, setFolderModifiedLangFilter] = useState(null);
    const [folderModifiedSortBy, setFolderModifiedSortBy] = useState('folder_asc'); // folder_asc, folder_desc, json_date_desc, json_date_asc, wav_date_desc, wav_date_asc
    const [folderModifiedRangeFrom, setFolderModifiedRangeFrom] = useState(null);
    const [folderModifiedRangeTo, setFolderModifiedRangeTo] = useState(null);
    const [folderModifiedFileTypeFilter, setFolderModifiedFileTypeFilter] = useState('all'); // all, has_wav, no_wav, has_json, no_json

    const handleSplitVideo = async () => {
        setSplitLoading(true);
        try {
            await splitVideo(projectId);
            message.success('Видео успешно разделено на части');
            setSplitCompleted(true);
        } catch (error) {
            message.error('Ошибка при разделении видео: ' + error.message);
        } finally {
            setSplitLoading(false);
        }
    };

    const openCreateFolderModal = () => {
        // Initialize with all folders selected
        if (project?.parts?.length > 0) {
            const allFolderNums = project.parts.map((_, index) => index + 1);
            setSelectedFolders(allFolderNums);
            setRangeTo(project.parts.length);
        }
        setModalVisible(true);
    };

    // Polling for task status - update ref every render to keep fresh closure
    fetchTaskStatusesRef.current = async () => {
        try {
            const result = await getTaskStatus(projectId);
            setTaskStatuses(result.tasks || {});
        } catch (error) {
            console.error('Error fetching task statuses:', error);
        }
    };

    const startPolling = useCallback(() => {
        // Initial fetch using ref for fresh closure
        fetchTaskStatusesRef.current();

        // Start polling every 3 seconds
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        // Use arrow function to always call latest version through ref
        pollingIntervalRef.current = setInterval(() => fetchTaskStatusesRef.current(), 3000);
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    // Load task statuses on page load and start polling if there are running tasks
    useEffect(() => {
        const initTaskStatuses = async () => {
            try {
                const result = await getTaskStatus(projectId);
                const tasks = result.tasks || {};
                setTaskStatuses(tasks);

                // If there are running tasks, start polling
                const hasRunningTasks = Object.values(tasks).some(t => t.status === 'running');
                if (hasRunningTasks) {
                    pollingIntervalRef.current = setInterval(() => fetchTaskStatusesRef.current(), 3000);
                }
            } catch (error) {
                console.error('Error loading initial task statuses:', error);
            }
        };

        initTaskStatuses();

        // Cleanup polling on unmount
        return () => stopPolling();
    }, [projectId, stopPolling]);

    const handleCreateFolder = async () => {
        if (selectedFolders.length === 0) {
            message.error('Выберите хотя бы одну папку');
            return;
        }

        setCreateFolderLoading(true);
        try {
            const result = await createFolderTranslateAndSynt(projectId, {
                folder_nums: selectedFolders,
                lang: targetLang,
                language_from: sourceLang,
                create_synt_audio: createSyntAudio,
                translation_model: translationModel,
                speed_synt_audio: speedSyntAudio,
                re_creation_synt_audio: reCreationSyntAudio,
                re_creation_translate: reCreationTranslate
            });
            message.success('Задача успешно запущена');
            setModalVisible(false);

            // Start polling for task status
            startPolling();
        } catch (error) {
            message.error('Ошибка: ' + error.message);
        } finally {
            setCreateFolderLoading(false);
        }
    };

    const handleSelectAll = () => {
        if (project?.parts?.length > 0) {
            setSelectedFolders(project.parts.map((_, index) => index + 1));
        }
    };

    const handleSelectNone = () => {
        setSelectedFolders([]);
    };

    const handleApplyRange = () => {
        const from = Math.max(1, rangeFrom);
        const to = Math.min(project?.parts?.length || 1, rangeTo);
        const range = [];
        for (let i = from; i <= to; i++) {
            range.push(i);
        }
        setSelectedFolders(range);
    };

    const toggleFolder = (folderNum) => {
        if (selectedFolders.includes(folderNum)) {
            setSelectedFolders(selectedFolders.filter(f => f !== folderNum));
        } else {
            setSelectedFolders([...selectedFolders, folderNum].sort((a, b) => a - b));
        }
    };

    // Merge Wavs and Video handlers
    const openMergeModal = () => {
        if (project?.parts?.length > 0) {
            const allFolderNums = project.parts.map((_, index) => index + 1);
            setMergeFolders(allFolderNums);
            setMergeRangeTo(project.parts.length);
        }
        setMergeModalVisible(true);
    };

    const handleMergeSelectAll = () => {
        if (project?.parts?.length > 0) {
            setMergeFolders(project.parts.map((_, index) => index + 1));
        }
    };

    const handleMergeSelectNone = () => {
        setMergeFolders([]);
    };

    const handleMergeApplyRange = () => {
        const from = Math.max(1, mergeRangeFrom);
        const to = Math.min(project?.parts?.length || 1, mergeRangeTo);
        const range = [];
        for (let i = from; i <= to; i++) {
            range.push(i);
        }
        setMergeFolders(range);
    };

    const toggleMergeFolder = (folderNum) => {
        if (mergeFolders.includes(folderNum)) {
            setMergeFolders(mergeFolders.filter(f => f !== folderNum));
        } else {
            setMergeFolders([...mergeFolders, folderNum].sort((a, b) => a - b));
        }
    };

    const handleMergeWavsAndVideo = async () => {
        if (mergeFolders.length === 0) {
            message.error('Выберите хотя бы одну папку');
            return;
        }

        setMergeLoading(true);
        try {
            await folderMergeWavsAndVideo(projectId, {
                lang: mergeLang,
                folder_nums: mergeFolders,
                create_merge_mp4_wav_folder: createMergeMp4WavFolder
            });
            message.success('Задача merge wavs and video успешно запущена');
            setMergeModalVisible(false);
            startPolling();
        } catch (error) {
            message.error('Ошибка: ' + error.message);
        } finally {
            setMergeLoading(false);
        }
    };

    // Cut Without Pauses handlers
    const openCutModal = () => {
        setCutModalVisible(true);
    };

    const handleCutWithoutPauses = async () => {
        setCutLoading(true);
        try {
            await cutWithoutPauses(projectId, {
                folder_num: cutFolderNum,
                lang: cutLang
            });
            message.success('Задача cut without pauses успешно запущена');
            setCutModalVisible(false);
            startPolling();
        } catch (error) {
            message.error('Ошибка: ' + error.message);
        } finally {
            setCutLoading(false);
        }
    };

    // Create Max Without Pauses Video handlers
    const openMaxVideoModal = () => {
        setMaxVideoModalVisible(true);
    };

    // Callback для обновления данных после bulk assign
    const handleBulkAssignRefresh = useCallback(async () => {
        try {
            const data = await fetchProjectFullData(projectId);
            setProject(data);
            message.success('Данные обновлены');
        } catch (error) {
            console.error('Error refreshing project data:', error);
            message.error('Ошибка обновления данных');
        }
    }, [projectId, fetchProjectFullData]);

    const handleCreateMaxVideo = async () => {
        setMaxVideoLoading(true);
        try {
            await createMaxWithoutPausesVideo(projectId, {
                lang: maxVideoLang,
                do_validate_completeness: doValidateCompleteness,
                force_recreate: forceRecreate,
                end_folder: endFolder
            });
            message.success('Задача create max without pauses video успешно запущена');
            setMaxVideoModalVisible(false);
            startPolling();
        } catch (error) {
            message.error('Ошибка: ' + error.message);
        } finally {
            setMaxVideoLoading(false);
        }
    };

    // Download links handlers
    const fetchDownloadLinks = async (lang = null) => {
        setDownloadLinksLoading(true);
        try {
            const links = await getDownloadLinks(projectId, lang);
            setDownloadLinks(links);
        } catch (error) {
            message.error('Ошибка загрузки ссылок: ' + error.message);
        } finally {
            setDownloadLinksLoading(false);
        }
    };

    const openDownloadDrawer = () => {
        setDownloadDrawerVisible(true);
        fetchDownloadLinks(downloadLangFilter);
    };

    const handleDownloadLangFilterChange = (lang) => {
        setDownloadLangFilter(lang);
        fetchDownloadLinks(lang);
    };

    // Folder Last Modified handlers
    const fetchFolderLastModified = async () => {
        setFolderModifiedLoading(true);
        try {
            const data = await getFolderLastModified(projectId);
            setFolderModifiedData(data);
        } catch (error) {
            message.error('Ошибка загрузки данных о папках: ' + error.message);
        } finally {
            setFolderModifiedLoading(false);
        }
    };

    const openFolderModifiedDrawer = () => {
        setFolderModifiedDrawerVisible(true);
        fetchFolderLastModified();
    };

    // Clips (cut-videos) handlers
    const fetchClips = async () => {
        setClipsLoading(true);
        try {
            const data = await getProjectClips(projectId);
            setClipsData(data);
        } catch (error) {
            message.error('Ошибка загрузки клипов: ' + error.message);
        } finally {
            setClipsLoading(false);
        }
    };

    const openClipsDrawer = () => {
        setClipsDrawerVisible(true);
        fetchClips();
    };

    // Ресинк статусов с диска (например, отметить eng auto_translation=done)
    const handleResyncFromDisk = async () => {
        setResyncLoading(true);
        try {
            await resyncProjectFromDisk(projectId);
            const data = await fetchProjectFullData(projectId);
            setProject(data);
            message.success('Статусы пересинхронизированы с диска');
        } catch (error) {
            message.error('Ошибка ресинка с диска: ' + error.message);
        } finally {
            setResyncLoading(false);
        }
    };

    const resetFolderModifiedFilters = () => {
        setFolderModifiedLangFilter(null);
        setFolderModifiedSortBy('folder_asc');
        setFolderModifiedRangeFrom(null);
        setFolderModifiedRangeTo(null);
        setFolderModifiedFileTypeFilter('all');
    };

    // Функция для фильтрации и сортировки данных папок
    const getProcessedFolderData = () => {
        if (!folderModifiedData?.folders) return [];

        let entries = Object.entries(folderModifiedData.folders);

        // Фильтр по диапазону папок
        if (folderModifiedRangeFrom !== null || folderModifiedRangeTo !== null) {
            entries = entries.filter(([folderNum]) => {
                const num = parseInt(folderNum);
                if (folderModifiedRangeFrom !== null && num < folderModifiedRangeFrom) return false;
                if (folderModifiedRangeTo !== null && num > folderModifiedRangeTo) return false;
                return true;
            });
        }

        // Фильтр по типу файлов
        if (folderModifiedFileTypeFilter !== 'all') {
            entries = entries.filter(([, languages]) => {
                const langData = folderModifiedLangFilter
                    ? languages[folderModifiedLangFilter]
                    : Object.values(languages)[0];

                if (!langData) return false;

                switch (folderModifiedFileTypeFilter) {
                    case 'has_wav':
                        return langData.wav_count > 0;
                    case 'no_wav':
                        return !langData.wav_count || langData.wav_count === 0;
                    case 'has_json':
                        return langData.json_count > 0;
                    case 'no_json':
                        return !langData.json_count || langData.json_count === 0;
                    default:
                        return true;
                }
            });
        }

        // Сортировка
        entries.sort(([aNum, aLangs], [bNum, bLangs]) => {
            const getDateForSort = (langs, type) => {
                const lang = folderModifiedLangFilter || Object.keys(langs)[0];
                const data = langs[lang];
                if (!data) return 0;
                const dateStr = type === 'json' ? data.json_last_modified : data.wav_last_modified;
                return dateStr ? new Date(dateStr).getTime() : 0;
            };

            switch (folderModifiedSortBy) {
                case 'folder_asc':
                    return parseInt(aNum) - parseInt(bNum);
                case 'folder_desc':
                    return parseInt(bNum) - parseInt(aNum);
                case 'json_date_desc':
                    return getDateForSort(bLangs, 'json') - getDateForSort(aLangs, 'json');
                case 'json_date_asc':
                    return getDateForSort(aLangs, 'json') - getDateForSort(bLangs, 'json');
                case 'wav_date_desc':
                    return getDateForSort(bLangs, 'wav') - getDateForSort(aLangs, 'wav');
                case 'wav_date_asc':
                    return getDateForSort(aLangs, 'wav') - getDateForSort(bLangs, 'wav');
                default:
                    return parseInt(aNum) - parseInt(bNum);
            }
        });

        return entries;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Filter artifacts by folder number
    const filterByFolder = (items) => {
        if (!items) return [];

        // If specific folder selected
        if (downloadFolderFilter !== null) {
            return items.filter(item => item.folder_num === downloadFolderFilter);
        }

        // If range selected
        if (downloadFolderRangeFrom !== null && downloadFolderRangeTo !== null) {
            return items.filter(item =>
                item.folder_num >= downloadFolderRangeFrom &&
                item.folder_num <= downloadFolderRangeTo
            );
        }

        return items;
    };

    const resetFolderFilters = () => {
        setDownloadFolderFilter(null);
        setDownloadFolderRangeFrom(null);
        setDownloadFolderRangeTo(null);
    };

    // Parts navigation functions
    const selectPartForView = (partId) => {
        setCurrentViewPartId(partId);
        setPartsDrawerVisible(false);
    };

    const getCurrentPartIndex = () => {
        if (!currentViewPartId || !project?.parts) return -1;
        return project.parts.findIndex(p => p.id === currentViewPartId);
    };

    const goToPrevPart = () => {
        const index = getCurrentPartIndex();
        if (index > 0) {
            setCurrentViewPartId(project.parts[index - 1].id);
        }
    };

    const goToNextPart = () => {
        const index = getCurrentPartIndex();
        if (index < project.parts.length - 1) {
            setCurrentViewPartId(project.parts[index + 1].id);
        }
    };

    const clearPartSelection = () => {
        setCurrentViewPartId(null);
    };

    const getCurrentPart = () => {
        if (!currentViewPartId || !project?.parts) return null;
        return project.parts.find(p => p.id === currentViewPartId);
    };

    const scrollToPart = (partId) => {
        setCurrentViewPartId(partId);
        setPartsDrawerVisible(false);
    };

    // Определяем ID частей, которые пропускаются (последние N)
    const getSkippedPartIds = () => {
        if (!project?.parts || !project.skip_last_parts) return new Set();
        const sorted = [...project.parts].sort((a, b) => parseInt(a.id) - parseInt(b.id));
        const skipCount = project.skip_last_parts;
        if (skipCount >= sorted.length) return new Set();
        return new Set(sorted.slice(sorted.length - skipCount).map(p => p.id));
    };

    const skippedPartIds = getSkippedPartIds();

    const getFilteredParts = () => {
        if (!project?.parts) return [];

        let filtered = project.parts;

        // Apply completion filter
        if (completionFilter !== 'all') {
            filtered = filtered.filter(part => {
                const status = getPartStatusSummary(part);
                const total = status.done + status.inProgress + status.pending;

                switch (completionFilter) {
                    case 'done':
                        // All tasks are done
                        return total > 0 && status.done === total;
                    case 'in_progress':
                        // Has at least one task in progress
                        return status.inProgress > 0;
                    case 'pending':
                        // All tasks are pending (nothing started)
                        return total > 0 && status.pending === total;
                    case 'partial':
                        // Has some done but not all (partial completion)
                        return status.done > 0 && status.done < total;
                    default:
                        return true;
                }
            });
        }

        // Apply search query filter
        if (partsSearchQuery) {
            const query = partsSearchQuery.toLowerCase();
            filtered = filtered.filter(part => {
                // Search by part ID/number
                if (part.id.toString().includes(query)) return true;
                // Search by language status
                if (part.languages?.some(lang =>
                    lang.id.toLowerCase().includes(query) ||
                    lang.auto_translation?.status?.toLowerCase().includes(query) ||
                    lang.voicing?.status?.toLowerCase().includes(query)
                )) return true;
                return false;
            });
        }

        return filtered;
    };

    const getPaginatedParts = () => {
        const filtered = getFilteredParts();
        const startIndex = (currentPartPage - 1) * partsPerPage;
        return filtered.slice(startIndex, startIndex + partsPerPage);
    };

    const getPartStatusSummary = (part) => {
        if (!part.languages || part.languages.length === 0) return { done: 0, inProgress: 0, pending: 0 };

        let done = 0, inProgress = 0, pending = 0;
        part.languages.forEach(lang => {
            const tasks = [
                lang.auto_translation, lang.translation_proofreading,
                lang.voicing, lang.voicing_proofreading,
                lang.animations_video, lang.animations_video_proofreading,
                lang.video, lang.video_proofreading,
                lang.full_video, lang.full_video_proofreading,
                lang.ts_playlist, lang.loading_in_s3
            ];
            tasks.forEach(task => {
                if (task?.status === 'done') done++;
                else if (task?.status === 'in_progress') inProgress++;
                else pending++;
            });
        });
        return { done, inProgress, pending };
    };

    const openPartPreview = (part) => {
        setSelectedPartForPreview(part);
        setPartPreviewVisible(true);
    };

    useEffect(() => {
        setLoading(true);
        fetchProjectFullData(projectId)
            .then((data) => {
                if (data) {
                    setProject(data);

                    // Показываем уведомление если данные были синхронизированы с диска
                    if (data._syncedFromDisk) {
                        const details = data._syncDetails;
                        if (details?.parts_created || details?.languages_created) {
                            message.success(
                                `Данные синхронизированы с диска: ${details.parts_created || 0} частей, ${details.languages_created || 0} языков`,
                                5
                            );
                        } else if (details?.fixes_applied) {
                            message.success(
                                `Синхронизация: применено ${details.fixes_applied} исправлений`,
                                5
                            );
                        } else {
                            message.success('Данные успешно синхронизированы с диска', 3);
                        }
                        console.log('[ProjectPage] Детали синхронизации:', details);
                    }

                    if (data.parts && data.parts.length > 0) {
                        console.log(`[ProjectPage] Проект загружен: ${data.name}, частей: ${data.parts.length}`);
                    }

                    // Загружаем длительность лекции
                    getLectureDuration(projectId).then(durationData => {
                        setLectureDuration(durationData);
                    });
                } else {
                    message.warning('Не удалось загрузить данные проекта. Проверьте консоль для деталей.');
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('[ProjectPage] Ошибка загрузки:', error);
                message.error('Ошибка загрузки проекта');
                setLoading(false);
            });
    }, [projectId, fetchProjectFullData, getLectureDuration]);

    if (loading) return <div className='_fcc _fv y _fgap2' style={{ minHeight: '80vh' }}>
        <Spin size="large" />
        <div >
            Загрузка...
        </div>
    </div>;
    if (!project) return <div>Project not found.</div>;

    // Проверяем, есть ли уже части у проекта
    const hasParts = project.parts && project.parts.length > 0;
    // Split выполнен если части уже есть ИЛИ мы только что выполнили split
    const isSplitDone = hasParts || splitCompleted;

    const folderCount = project?.parts?.length || 0;

    return (<>
        <Head onLogout={onLogout}>
            <TableSettingsLangs value={langsShown} handleChange={setLangsShown} />
        </Head>

        <div className="_fv _fgap2" style={{ padding: '0 2em 0 2em', flexGrow: 1, background: '#e9e9e9' }}>

            <div className="_fv _fgap02" >
                <h3 style={{ margin: 0 }}>{project.name}</h3>
                <div className="_fh _fgap2" style={{ alignItems: 'center' }}>
                    <div><b>Project ID:</b> {project.id}</div>
                    {lectureDuration?.duration_formatted && (
                        <div style={{
                            background: '#1890ff',
                            color: 'white',
                            padding: '2px 10px',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}>
                            <ClockCircleOutlined />
                            {lectureDuration.duration_formatted}
                        </div>
                    )}
                </div>

                {/* Lecture overview: per-language stats */}
                {project.parts && project.parts.length > 0 && (() => {
                    const totalParts = project.parts.length;
                    const skipLastParts = project.skip_last_parts || 0;
                    // Сортируем части по id для корректного определения последних
                    const sortedParts = [...project.parts].sort((a, b) => parseInt(a.id) - parseInt(b.id));
                    const activeParts = skipLastParts > 0 ? sortedParts.slice(0, sortedParts.length - skipLastParts) : sortedParts;
                    // Collect all languages across active parts only
                    const langStats = {};
                    activeParts.forEach(part => {
                        (part.languages || []).forEach(lang => {
                            if (!langStats[lang.id]) {
                                langStats[lang.id] = { translation: { done: 0, inProgress: 0, total: 0 }, voicing: { done: 0, inProgress: 0, total: 0 }, video: { done: 0, inProgress: 0, total: 0 } };
                            }
                            const s = langStats[lang.id];
                            // Translation: auto_translation + translation_proofreading both done = part done
                            s.translation.total++;
                            if (lang.auto_translation?.status === 'done' && lang.translation_proofreading?.status === 'done') s.translation.done++;
                            else if (lang.auto_translation?.status === 'in_progress' || lang.translation_proofreading?.status === 'in_progress' || lang.auto_translation?.status === 'done' || lang.translation_proofreading?.status === 'done') s.translation.inProgress++;
                            // Voicing
                            s.voicing.total++;
                            if (lang.voicing?.status === 'done' && lang.voicing_proofreading?.status === 'done') s.voicing.done++;
                            else if (lang.voicing?.status === 'in_progress' || lang.voicing_proofreading?.status === 'in_progress' || lang.voicing?.status === 'done' || lang.voicing_proofreading?.status === 'done') s.voicing.inProgress++;
                            // Video
                            s.video.total++;
                            if (lang.full_video?.status === 'done') s.video.done++;
                            else if (lang.full_video?.status === 'in_progress') s.video.inProgress++;
                        });
                    });
                    const langIds = Object.keys(langStats);
                    if (langIds.length === 0) return null;
                    return (
                        <div style={{
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginTop: '8px',
                            border: '1px solid #e8e8e8'
                        }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#595959', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <span>Частей: {totalParts}{skipLastParts > 0 ? ` (учитывается: ${activeParts.length})` : ''}</span>
                                {skipLastParts > 0 && (
                                    <Tooltip title="Определяется автоматически при первом переводе лекции. Пропущенные части не влияют на статус завершённости.">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 400, color: '#8c8c8c' }}>
                                            Пропущено последних: {skipLastParts}
                                            <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {langIds.map(langId => {
                                    const s = langStats[langId];
                                    const MiniBar = ({ done, inProgress, total, label }) => {
                                        const pDone = total > 0 ? (done / total) * 100 : 0;
                                        const pProgress = total > 0 ? (inProgress / total) * 100 : 0;
                                        return (
                                            <Tooltip title={`${label}: ${done} готово, ${inProgress} в работе, ${total - done - inProgress} ожидает`}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '10px' }}>
                                                    <span style={{ color: '#8c8c8c', minWidth: '10px' }}>{label}</span>
                                                    <div style={{ width: '30px', height: '5px', background: '#f0f0f0', borderRadius: '3px', overflow: 'hidden', display: 'flex' }}>
                                                        <div style={{ width: `${pDone}%`, height: '100%', background: '#52c41a' }} />
                                                        <div style={{ width: `${pProgress}%`, height: '100%', background: '#faad14' }} />
                                                    </div>
                                                    <span style={{ color: done === total ? '#52c41a' : '#595959', fontSize: '9px' }}>{done}/{total}</span>
                                                </div>
                                            </Tooltip>
                                        );
                                    };
                                    return (
                                        <div key={langId} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '3px 6px',
                                            background: '#fafafa',
                                            borderRadius: '6px',
                                            border: '1px solid #f0f0f0'
                                        }}>
                                            <Flag langId={langId} height={12} />
                                            <span style={{ fontWeight: 500, fontSize: '11px' }}>{getLanguageName(langId)}</span>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                                <MiniBar {...s.translation} label="П" />
                                                <MiniBar {...s.voicing} label="О" />
                                                <MiniBar {...s.video} label="В" />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                <div className="_fh _fgap1" style={{ marginTop: '1em' }}>
                    <Button
                        type="default"
                        icon={<DownloadOutlined />}
                        onClick={openDownloadDrawer}
                    >
                        Downloads
                    </Button>
                    <Button
                        type="default"
                        icon={<ClockCircleOutlined />}
                        onClick={openFolderModifiedDrawer}
                    >
                        Даты изменений
                    </Button>
                    <Button
                        type="default"
                        icon={<PlayCircleOutlined />}
                        onClick={openClipsDrawer}
                    >
                        Клипы
                    </Button>
                    <Button
                        type="default"
                        icon={<SyncOutlined />}
                        loading={resyncLoading}
                        onClick={handleResyncFromDisk}
                    >
                        Ресинк с диска
                    </Button>
                    {isAdminOrSuperAdmin && (
                        <Button
                            type="default"
                            icon={<LinkOutlined />}
                            href={`https://translate-auto-generated.dev.torsunov.pro/project/${projectId}`}
                            target="_blank"
                        >
                            Админка
                        </Button>
                    )}
                    {isAdminOrSuperAdmin && (
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Удалить лекцию?',
                                    icon: <ExclamationCircleOutlined />,
                                    content: (
                                        <div>
                                            <p>Вы уверены, что хотите удалить лекцию <b>{project?.name}</b>?</p>
                                            <p style={{ color: 'red' }}>Это действие необратимо! Будут удалены все файлы с диска и записи из базы данных.</p>
                                        </div>
                                    ),
                                    okText: 'Удалить',
                                    okType: 'danger',
                                    cancelText: 'Отмена',
                                    onOk: async () => {
                                        try {
                                            await deleteProject(projectId);
                                            message.success('Лекция успешно удалена');
                                            navigate('/');
                                        } catch (error) {
                                            message.error('Ошибка при удалении: ' + error.message);
                                        }
                                    },
                                });
                            }}
                        >
                            Удалить лекцию
                        </Button>
                    )}
                </div>

                {/* Task Status Panel - collapsible, collapsed by default */}
                <Collapse
                    activeKey={taskStatusPanelKeys}
                    onChange={setTaskStatusPanelKeys}
                    style={{ marginTop: '1em' }}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    items={[{
                        key: 'task-status',
                        label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span style={{ fontWeight: 500 }}>Task Status ({Object.keys(taskStatuses).length})</span>
                                <Button
                                    size="small"
                                    icon={<SyncOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fetchTaskStatusesRef.current();
                                    }}
                                >
                                    Refresh
                                </Button>
                            </div>
                        ),
                        children: Object.keys(taskStatuses).length === 0 ? (
                            <div style={{ color: '#888', textAlign: 'center', padding: 20 }}>
                                Нет активных задач
                            </div>
                        ) : (
                            Object.entries(taskStatuses).map(([taskId, task]) => (
                                <div
                                    key={taskId}
                                    style={{
                                        padding: 12,
                                        marginBottom: 8,
                                        background: task.status === 'running' ? '#e6f7ff' : task.status === 'success' ? '#f6ffed' : '#fff2f0',
                                        borderRadius: 6,
                                        border: `1px solid ${task.status === 'running' ? '#91d5ff' : task.status === 'success' ? '#b7eb8f' : '#ffa39e'}`
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {task.status === 'running' && <LoadingOutlined style={{ color: '#1890ff' }} />}
                                            {task.status === 'success' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                            {task.status === 'interrupted' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                            <span style={{ fontWeight: 'bold' }}>{TASK_NAMES[task.task_name] || task.task_name || 'Task'}</span>
                                            <Tag color={task.status === 'running' ? 'processing' : task.status === 'success' ? 'success' : 'error'}>
                                                {task.status}
                                            </Tag>
                                            {['success', 'error', 'interrupted'].includes(task.status) && (
                                                <Button
                                                    size="small"
                                                    type="link"
                                                    onClick={() => setSelectedTaskForDetails({ ...task, id: taskId })}
                                                >
                                                    Подробности
                                                </Button>
                                            )}
                                        </div>
                                        <span style={{ fontSize: 12, color: '#888' }}>ID: {taskId}</span>
                                    </div>
                                    {task.status === 'running' && (
                                        <Progress
                                            percent={task.progress || 0}
                                            size="small"
                                            style={{ marginTop: 8 }}
                                            status="active"
                                        />
                                    )}
                                    {(task.started_at || task.finished_at) && (
                                        <div style={{ fontSize: 12, color: '#666', marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                                            {task.started_at && (
                                                <span>
                                                    <strong>Начато:</strong> {new Date(task.started_at).toLocaleString()}
                                                </span>
                                            )}
                                            {task.finished_at && (
                                                <span>
                                                    <strong>Завершено:</strong> {new Date(task.finished_at).toLocaleString()}
                                                </span>
                                            )}
                                            {task.started_at && task.finished_at && (
                                                <span style={{ color: '#1890ff' }}>
                                                    <strong>Длительность:</strong> {(() => {
                                                        const start = new Date(task.started_at);
                                                        const end = new Date(task.finished_at);
                                                        const diffMs = end - start;
                                                        const diffSec = Math.floor(diffMs / 1000);
                                                        const hours = Math.floor(diffSec / 3600);
                                                        const minutes = Math.floor((diffSec % 3600) / 60);
                                                        const seconds = diffSec % 60;
                                                        if (hours > 0) return `${hours}ч ${minutes}м ${seconds}с`;
                                                        if (minutes > 0) return `${minutes}м ${seconds}с`;
                                                        return `${seconds}с`;
                                                    })()}
                                                </span>
                                            )}
                                            {task.started_at && !task.finished_at && task.status === 'running' && (
                                                <span style={{ color: '#1890ff' }}>
                                                    <strong>В процессе:</strong> {(() => {
                                                        const start = new Date(task.started_at);
                                                        const now = new Date();
                                                        const diffMs = now - start;
                                                        const diffSec = Math.floor(diffMs / 1000);
                                                        const hours = Math.floor(diffSec / 3600);
                                                        const minutes = Math.floor((diffSec % 3600) / 60);
                                                        const seconds = diffSec % 60;
                                                        if (hours > 0) return `${hours}ч ${minutes}м ${seconds}с`;
                                                        if (minutes > 0) return `${minutes}м ${seconds}с`;
                                                        return `${seconds}с`;
                                                    })()}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {task.error && (
                                        <Alert
                                            type="error"
                                            message={task.error.message}
                                            style={{ marginTop: 8 }}
                                            size="small"
                                        />
                                    )}
                                </div>
                            ))
                        )
                    }]}
                />

                {/* Task Details Modal */}
                <Modal
                    title={`Детали задачи: ${selectedTaskForDetails ? (TASK_NAMES[selectedTaskForDetails.task_name] || selectedTaskForDetails.task_name || 'Task') : ''}`}
                    open={!!selectedTaskForDetails}
                    onCancel={() => setSelectedTaskForDetails(null)}
                    footer={[
                        <Button key="close" type="primary" onClick={() => setSelectedTaskForDetails(null)}>
                            Закрыть
                        </Button>
                    ]}
                    width={700}
                >
                    {selectedTaskForDetails && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div>
                                <strong>ID задачи:</strong> <code>{selectedTaskForDetails.task_id || selectedTaskForDetails.id}</code>
                            </div>
                            <div>
                                <strong>Статус:</strong>{' '}
                                <Tag color={selectedTaskForDetails.status === 'success' ? 'success' : selectedTaskForDetails.status === 'running' ? 'processing' : 'error'}>
                                    {selectedTaskForDetails.status}
                                </Tag>
                            </div>
                            {selectedTaskForDetails.started_at && (
                                <div>
                                    <strong>Начало:</strong> {new Date(selectedTaskForDetails.started_at).toLocaleString()}
                                </div>
                            )}
                            {selectedTaskForDetails.finished_at && (
                                <div>
                                    <strong>Окончание:</strong> {new Date(selectedTaskForDetails.finished_at).toLocaleString()}
                                </div>
                            )}
                            {selectedTaskForDetails.progress !== undefined && (
                                <div>
                                    <strong>Прогресс:</strong> {selectedTaskForDetails.progress}%
                                </div>
                            )}
                            {selectedTaskForDetails.langs && selectedTaskForDetails.langs.length > 0 && (
                                <div>
                                    <strong>Языки:</strong> {Array.isArray(selectedTaskForDetails.langs) ? selectedTaskForDetails.langs.join(', ') : selectedTaskForDetails.langs}
                                </div>
                            )}
                            {selectedTaskForDetails.folders && (Array.isArray(selectedTaskForDetails.folders) ? selectedTaskForDetails.folders.length > 0 : true) && (
                                <div>
                                    <strong>{Array.isArray(selectedTaskForDetails.folders) && selectedTaskForDetails.folders.length === 1 ? 'Папка' : 'Папки'}:</strong>{' '}
                                    {Array.isArray(selectedTaskForDetails.folders) ? selectedTaskForDetails.folders.join(', ') : selectedTaskForDetails.folders}
                                </div>
                            )}
                            {selectedTaskForDetails.params && Object.keys(selectedTaskForDetails.params).length > 0 && (
                                <div>
                                    <strong>Параметры:</strong>
                                    <pre style={{
                                        background: '#f5f5f5',
                                        color: '#333',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 6,
                                        padding: 8,
                                        maxHeight: 150,
                                        overflow: 'auto',
                                        fontSize: 12,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        margin: '4px 0 0 0'
                                    }}>
                                        {JSON.stringify(selectedTaskForDetails.params, null, 2)}
                                    </pre>
                                </div>
                            )}
                            {(selectedTaskForDetails.status === 'error' || selectedTaskForDetails.status === 'interrupted' || selectedTaskForDetails.error) && selectedTaskForDetails.error && (
                                <>
                                    <div>
                                        <strong>Ошибка:</strong>
                                        <Alert
                                            type="error"
                                            message={selectedTaskForDetails.error.message || 'Неизвестная ошибка'}
                                            style={{ marginTop: 4 }}
                                        />
                                    </div>
                                    {selectedTaskForDetails.error.traceback && (
                                        <div>
                                            <strong>Traceback:</strong>
                                            <pre style={{
                                                background: '#fff0f0',
                                                color: '#a00',
                                                border: '1px solid #fcc',
                                                borderRadius: 6,
                                                padding: 12,
                                                maxHeight: 300,
                                                overflow: 'auto',
                                                fontSize: 12,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                margin: '4px 0 0 0'
                                            }}>
                                                {selectedTaskForDetails.error.traceback}
                                            </pre>
                                        </div>
                                    )}
                                </>
                            )}
                            {selectedTaskForDetails.result !== null && selectedTaskForDetails.result !== undefined && (
                                <div>
                                    <strong>Результат:</strong>
                                    <pre style={{
                                        background: '#f5f5f5',
                                        color: '#333',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 6,
                                        padding: 12,
                                        maxHeight: 300,
                                        overflow: 'auto',
                                        fontSize: 12,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        margin: '4px 0 0 0'
                                    }}>
                                        {typeof selectedTaskForDetails.result === 'object' ? JSON.stringify(selectedTaskForDetails.result, null, 2) : String(selectedTaskForDetails.result)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                {/* Quick part navigation */}
                {hasParts && (
                    <div className="_fv _fgap05" style={{ marginTop: '0.5em' }}>
                        <div className="_fh _fgap1" style={{ alignItems: 'center' }}>
                            <span style={{ color: '#666' }}>View part:</span>
                            <Select
                                showSearch
                                placeholder="Search part..."
                                style={{ width: 200 }}
                                value={currentViewPartId}
                                onChange={(value) => selectPartForView(value)}
                                filterOption={(input, option) =>
                                    option.label.toLowerCase().includes(input.toLowerCase())
                                }
                                options={project.parts.map(part => ({
                                    value: part.id,
                                    label: `Part ${part.id}`
                                }))}
                                allowClear
                                onClear={clearPartSelection}
                            />
                            <Button
                                size="small"
                                icon={<LeftOutlined />}
                                onClick={goToPrevPart}
                                disabled={!currentViewPartId || getCurrentPartIndex() <= 0}
                            />
                            <span style={{ color: '#666', minWidth: 80, textAlign: 'center' }}>
                                {currentViewPartId ? `${getCurrentPartIndex() + 1} / ${folderCount}` : `— / ${folderCount}`}
                            </span>
                            <Button
                                size="small"
                                icon={<RightOutlined />}
                                onClick={goToNextPart}
                                disabled={!currentViewPartId || getCurrentPartIndex() >= project.parts.length - 1}
                            />
                            {currentViewPartId && (
                                <Button size="small" onClick={clearPartSelection}>
                                    Show All
                                </Button>
                            )}
                        </div>
                        <div className="_fh _fgap1" style={{ alignItems: 'center' }}>
                            <Input
                                placeholder="Search parts..."
                                prefix={<SearchOutlined />}
                                value={partsSearchQuery}
                                onChange={(e) => {
                                    setPartsSearchQuery(e.target.value);
                                    setCurrentPartPage(1);
                                }}
                                style={{ width: 200 }}
                                allowClear
                                size="small"
                            />
                            <span style={{ color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <FilterOutlined /> Filter:
                            </span>
                            <Radio.Group
                                value={completionFilter}
                                onChange={(e) => {
                                    setCompletionFilter(e.target.value);
                                    setCurrentPartPage(1);
                                }}
                                optionType="button"
                                buttonStyle="solid"
                                size="small"
                            >
                                <Radio.Button value="all">All</Radio.Button>
                                <Radio.Button value="done">Done</Radio.Button>
                                <Radio.Button value="in_progress">In Progress</Radio.Button>
                                <Radio.Button value="pending">Pending</Radio.Button>
                                <Radio.Button value="partial">Partial</Radio.Button>
                            </Radio.Group>
                            {(completionFilter !== 'all' || partsSearchQuery) && (
                                <Tag color="blue">{getFilteredParts().length} of {folderCount}</Tag>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Selected Part View - shows inline when a part is selected */}
            {currentViewPartId && getCurrentPart() && (
                <div style={{
                    background: '#fff',
                    borderRadius: 8,
                    padding: 16,
                    border: '2px solid #1890ff',
                    marginBottom: 16
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                        paddingBottom: 12,
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <h3 style={{ margin: 0 }}>Part {currentViewPartId}</h3>
                            <Tag color="blue">{getCurrentPartIndex() + 1} of {folderCount}</Tag>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                                icon={<LeftOutlined />}
                                onClick={goToPrevPart}
                                disabled={getCurrentPartIndex() <= 0}
                            >
                                Prev
                            </Button>
                            <Button
                                icon={<RightOutlined />}
                                onClick={goToNextPart}
                                disabled={getCurrentPartIndex() >= project.parts.length - 1}
                            >
                                Next
                            </Button>
                            <Button onClick={clearPartSelection}>
                                Show All Parts
                            </Button>
                        </div>
                    </div>
                    <TableProject
                        {...project}
                        parts={[getCurrentPart()]}
                        pageMode={true}
                        onDataRefresh={handleBulkAssignRefresh}
                        partsDuration={lectureDuration?.parts}
                        langsShown={langsShown}
                    />
                </div>
            )}

            {/* Show all parts when no specific part is selected */}
            {!currentViewPartId && (
                <TableProject {...project} parts={getFilteredParts()} pageMode={true} onDataRefresh={handleBulkAssignRefresh} partsDuration={lectureDuration?.parts} langsShown={langsShown} />
            )}

        </div>

        {/* Modal for Create Folder settings */}
        <Modal
            title="Configure Translation Task"
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={[
                <Button key="cancel" onClick={() => setModalVisible(false)}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={createFolderLoading}
                    onClick={handleCreateFolder}
                >
                    Start Task
                </Button>
            ]}
            width={500}
        >
            {/* Folder Selection */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Select Folders (folder_nums)</div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Checkbox
                        checked={selectedFolders.length === folderCount && folderCount > 0}
                        indeterminate={selectedFolders.length > 0 && selectedFolders.length < folderCount}
                        onChange={(e) => e.target.checked ? handleSelectAll() : handleSelectNone()}
                    >
                        Select All
                    </Checkbox>
                    <Checkbox
                        checked={selectedFolders.length === 0}
                        onChange={() => handleSelectNone()}
                    >
                        Select None
                    </Checkbox>
                    <span style={{ marginLeft: 8 }}>| Range:</span>
                    <InputNumber
                        size="small"
                        min={1}
                        max={folderCount}
                        value={rangeFrom}
                        onChange={setRangeFrom}
                        style={{ width: 60 }}
                    />
                    <span>to</span>
                    <InputNumber
                        size="small"
                        min={1}
                        max={folderCount}
                        value={rangeTo}
                        onChange={setRangeTo}
                        style={{ width: 60 }}
                    />
                    <Button size="small" onClick={handleApplyRange}>Apply Range</Button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 100, overflowY: 'auto', padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    {Array.from({ length: folderCount }, (_, i) => i + 1).map(num => (
                        <Button
                            key={num}
                            size="small"
                            type={selectedFolders.includes(num) ? 'primary' : 'default'}
                            onClick={() => toggleFolder(num)}
                            style={{ minWidth: 32 }}
                        >
                            {num}
                        </Button>
                    ))}
                </div>
                <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                    Selected: {selectedFolders.length} of {folderCount} folders
                </div>
            </div>

            {/* Target Language */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    <span style={{ color: 'red' }}>*</span> Target Language (lang)
                </div>
                <Select
                    value={targetLang}
                    onChange={setTargetLang}
                    style={{ width: '100%' }}
                    options={LANGUAGES}
                />
            </div>

            {/* Source Language */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    <span style={{ color: 'red' }}>*</span> Source Language (language_from)
                </div>
                <Select
                    value={sourceLang}
                    onChange={setSourceLang}
                    style={{ width: '100%' }}
                    options={LANGUAGES}
                />
            </div>

            {/* Translation Model */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Translation Model</div>
                <Select
                    value={translationModel}
                    onChange={setTranslationModel}
                    style={{ width: '100%' }}
                    options={TRANSLATION_MODELS}
                />
            </div>

            {/* Create Synthesized Audio */}
            <div style={{ marginBottom: 16 }}>
                <Checkbox
                    checked={createSyntAudio}
                    onChange={(e) => setCreateSyntAudio(e.target.checked)}
                >
                    Create synthesized audio (create_synt_audio)
                </Checkbox>
            </div>

            {/* Audio Speed */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Audio Speed (speed_synt_audio)</div>
                <InputNumber
                    value={speedSyntAudio}
                    onChange={setSpeedSyntAudio}
                    min={0.5}
                    max={2}
                    step={0.1}
                    style={{ width: 100 }}
                />
            </div>

            {/* Re-create existing translations */}
            <div style={{ marginBottom: 16 }}>
                <Checkbox
                    checked={reCreationTranslate}
                    onChange={(e) => setReCreationTranslate(e.target.checked)}
                >
                    Re-create existing translations (re_creation_translate)
                </Checkbox>
            </div>

            {/* Re-create existing audio */}
            <div style={{ marginBottom: 16 }}>
                <Checkbox
                    checked={reCreationSyntAudio}
                    onChange={(e) => setReCreationSyntAudio(e.target.checked)}
                >
                    Re-create existing audio (re_creation_synt_audio)
                </Checkbox>
            </div>
        </Modal>

        {/* Parts Navigation Drawer */}
        <Drawer
            title={`Parts Navigation (${getFilteredParts().length}${completionFilter !== 'all' || partsSearchQuery ? ` of ${folderCount}` : ''} parts)`}
            placement="right"
            width={450}
            open={partsDrawerVisible}
            onClose={() => setPartsDrawerVisible(false)}
        >
            {/* Search */}
            <Input
                placeholder="Search by part number, language, status..."
                prefix={<SearchOutlined />}
                value={partsSearchQuery}
                onChange={(e) => {
                    setPartsSearchQuery(e.target.value);
                    setCurrentPartPage(1);
                }}
                style={{ marginBottom: 12 }}
                allowClear
            />

            {/* Completion Filter */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FilterOutlined /> Filter by completion:
                </div>
                <Radio.Group
                    value={completionFilter}
                    onChange={(e) => {
                        setCompletionFilter(e.target.value);
                        setCurrentPartPage(1);
                    }}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                >
                    <Radio.Button value="all">All</Radio.Button>
                    <Radio.Button value="done">Done</Radio.Button>
                    <Radio.Button value="in_progress">In Progress</Radio.Button>
                    <Radio.Button value="pending">Pending</Radio.Button>
                    <Radio.Button value="partial">Partial</Radio.Button>
                </Radio.Group>
            </div>

            {/* Parts List */}
            <List
                dataSource={getPaginatedParts()}
                renderItem={(part) => {
                    const status = getPartStatusSummary(part);
                    return (
                        <List.Item
                            style={{
                                cursor: 'pointer',
                                padding: '12px',
                                borderRadius: 8,
                                marginBottom: 8,
                                border: '1px solid #f0f0f0',
                                background: '#fafafa'
                            }}
                            actions={[
                                <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openPartPreview(part);
                                    }}
                                >
                                    Preview
                                </Button>,
                                <Button
                                    size="small"
                                    type="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        selectPartForView(part.id);
                                    }}
                                >
                                    View
                                </Button>
                            ]}
                        >
                            <List.Item.Meta
                                title={
                                    <span style={{ fontWeight: 'bold', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        Part {part.id}
                                        {lectureDuration?.parts?.[String(part.id)]?.duration_formatted && (
                                            <Tag color="blue" style={{ fontWeight: 'normal', fontSize: 12 }}>
                                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                {lectureDuration.parts[String(part.id)].duration_formatted}
                                            </Tag>
                                        )}
                                    </span>
                                }
                                description={
                                    <div style={{ marginTop: 4 }}>
                                        <Tag color="green">{status.done} done</Tag>
                                        <Tag color="blue">{status.inProgress} in progress</Tag>
                                        <Tag color="default">{status.pending} pending</Tag>
                                        <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                                            Languages: {part.languages?.map(l => l.id).join(', ') || 'none'}
                                        </div>
                                    </div>
                                }
                            />
                        </List.Item>
                    );
                }}
            />

            {/* Pagination */}
            {getFilteredParts().length > partsPerPage && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Pagination
                        current={currentPartPage}
                        total={getFilteredParts().length}
                        pageSize={partsPerPage}
                        onChange={setCurrentPartPage}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total) => `Total ${total} parts`}
                    />
                </div>
            )}
        </Drawer>

        {/* Part Preview Modal */}
        <Modal
            title={`Part ${selectedPartForPreview?.id} - Preview`}
            open={partPreviewVisible}
            onCancel={() => setPartPreviewVisible(false)}
            footer={[
                <Button key="goto" type="primary" onClick={() => {
                    selectPartForView(selectedPartForPreview?.id);
                    setPartPreviewVisible(false);
                }}>
                    View Part
                </Button>,
                <Button key="close" onClick={() => setPartPreviewVisible(false)}>
                    Close
                </Button>
            ]}
            width={600}
        >
            {selectedPartForPreview && (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <strong>Part ID:</strong> {selectedPartForPreview.id}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <strong>Languages & Tasks:</strong>
                    </div>

                    {selectedPartForPreview.languages?.map(lang => (
                        <Card
                            key={lang.id}
                            size="small"
                            title={<span style={{ textTransform: 'uppercase' }}>{lang.id}</span>}
                            style={{ marginBottom: 8 }}
                        >
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div>
                                    <strong>Auto Translation:</strong>{' '}
                                    <Tag color={lang.auto_translation?.status === 'done' ? 'green' : lang.auto_translation?.status === 'in_progress' ? 'blue' : 'default'}>
                                        {lang.auto_translation?.status || 'pending'}
                                    </Tag>
                                </div>
                                <div>
                                    <strong>Voicing:</strong>{' '}
                                    <Tag color={lang.voicing?.status === 'done' ? 'green' : lang.voicing?.status === 'in_progress' ? 'blue' : 'default'}>
                                        {lang.voicing?.status || 'pending'}
                                    </Tag>
                                </div>
                                <div>
                                    <strong>Voicing Check:</strong>{' '}
                                    <Tag color={lang.voicing_proofreading?.status === 'done' ? 'green' : lang.voicing_proofreading?.status === 'in_progress' ? 'blue' : 'default'}>
                                        {lang.voicing_proofreading?.status || 'pending'}
                                    </Tag>
                                </div>
                                <div>
                                    <strong>Video:</strong>{' '}
                                    <Tag color={lang.animations_video?.status === 'done' ? 'green' : lang.animations_video?.status === 'in_progress' ? 'blue' : 'default'}>
                                        {lang.animations_video?.status || 'pending'}
                                    </Tag>
                                </div>
                            </div>
                        </Card>
                    ))}

                    {(!selectedPartForPreview.languages || selectedPartForPreview.languages.length === 0) && (
                        <div style={{ color: '#888', textAlign: 'center', padding: 20 }}>
                            No languages configured for this part
                        </div>
                    )}
                </div>
            )}
        </Modal>

        {/* Merge Wavs and Video Modal */}
        <Modal
            title="Merge Wavs and Video"
            open={mergeModalVisible}
            onCancel={() => setMergeModalVisible(false)}
            footer={[
                <Button key="cancel" onClick={() => setMergeModalVisible(false)}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={mergeLoading}
                    onClick={handleMergeWavsAndVideo}
                >
                    Start Task
                </Button>
            ]}
            width={500}
        >
            {/* Language */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    <span style={{ color: 'red' }}>*</span> Language (lang)
                </div>
                <Select
                    value={mergeLang}
                    onChange={setMergeLang}
                    style={{ width: '100%' }}
                    options={LANGUAGES}
                />
            </div>

            {/* Folder Selection */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Select Folders (folder_nums)</div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Checkbox
                        checked={mergeFolders.length === folderCount && folderCount > 0}
                        indeterminate={mergeFolders.length > 0 && mergeFolders.length < folderCount}
                        onChange={(e) => e.target.checked ? handleMergeSelectAll() : handleMergeSelectNone()}
                    >
                        Select All
                    </Checkbox>
                    <Checkbox
                        checked={mergeFolders.length === 0}
                        onChange={() => handleMergeSelectNone()}
                    >
                        Select None
                    </Checkbox>
                    <span style={{ marginLeft: 8 }}>| Range:</span>
                    <InputNumber
                        size="small"
                        min={1}
                        max={folderCount}
                        value={mergeRangeFrom}
                        onChange={setMergeRangeFrom}
                        style={{ width: 60 }}
                    />
                    <span>to</span>
                    <InputNumber
                        size="small"
                        min={1}
                        max={folderCount}
                        value={mergeRangeTo}
                        onChange={setMergeRangeTo}
                        style={{ width: 60 }}
                    />
                    <Button size="small" onClick={handleMergeApplyRange}>Apply Range</Button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 100, overflowY: 'auto', padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    {Array.from({ length: folderCount }, (_, i) => i + 1).map(num => (
                        <Button
                            key={num}
                            size="small"
                            type={mergeFolders.includes(num) ? 'primary' : 'default'}
                            onClick={() => toggleMergeFolder(num)}
                            style={{ minWidth: 32 }}
                        >
                            {num}
                        </Button>
                    ))}
                </div>
                <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                    Selected: {mergeFolders.length} of {folderCount} folders
                </div>
            </div>

            {/* Create Merge MP4 WAV Folder */}
            <div style={{ marginBottom: 16 }}>
                <Checkbox
                    checked={createMergeMp4WavFolder}
                    onChange={(e) => setCreateMergeMp4WavFolder(e.target.checked)}
                >
                    Create merge mp4 wav folder (create_merge_mp4_wav_folder)
                </Checkbox>
            </div>
        </Modal>

        {/* Cut Without Pauses Modal */}
        <Modal
            title="Cut Without Pauses"
            open={cutModalVisible}
            onCancel={() => setCutModalVisible(false)}
            footer={[
                <Button key="cancel" onClick={() => setCutModalVisible(false)}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={cutLoading}
                    onClick={handleCutWithoutPauses}
                >
                    Start Task
                </Button>
            ]}
            width={400}
        >
            {/* Language */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    <span style={{ color: 'red' }}>*</span> Language (lang)
                </div>
                <Select
                    value={cutLang}
                    onChange={setCutLang}
                    style={{ width: '100%' }}
                    options={LANGUAGES}
                />
            </div>

            {/* Folder Number */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    <span style={{ color: 'red' }}>*</span> Folder Number (folder_num)
                </div>
                <InputNumber
                    value={cutFolderNum}
                    onChange={setCutFolderNum}
                    min={1}
                    max={folderCount}
                    style={{ width: '100%' }}
                />
            </div>
        </Modal>

        {/* Create Max Without Pauses Video Modal */}
        <Modal
            title="Create Max Without Pauses Video"
            open={maxVideoModalVisible}
            onCancel={() => setMaxVideoModalVisible(false)}
            footer={[
                <Button key="cancel" onClick={() => setMaxVideoModalVisible(false)}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={maxVideoLoading}
                    onClick={handleCreateMaxVideo}
                >
                    Start Task
                </Button>
            ]}
            width={400}
        >
            {/* Language */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    <span style={{ color: 'red' }}>*</span> Language (lang)
                </div>
                <Select
                    value={maxVideoLang}
                    onChange={setMaxVideoLang}
                    style={{ width: '100%' }}
                    options={LANGUAGES}
                />
            </div>

            {/* End Folder */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    End Folder (end_folder)
                </div>
                <InputNumber
                    value={endFolder}
                    onChange={setEndFolder}
                    min={1}
                    max={folderCount}
                    style={{ width: '100%' }}
                    placeholder="До максимума (все папки)"
                />
                <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                    Ограничить обработку до указанной папки. Пусто = все папки.
                </div>
            </div>

            {/* Force Recreate */}
            <div style={{ marginBottom: 16 }}>
                <Checkbox
                    checked={forceRecreate}
                    onChange={(e) => setForceRecreate(e.target.checked)}
                >
                    Force recreate (force_recreate)
                </Checkbox>
                <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                    Пропустить проверку существующего видео и создать заново
                </div>
            </div>

            {/* Validate Completeness */}
            <div style={{ marginBottom: 16 }}>
                <Checkbox
                    checked={doValidateCompleteness}
                    onChange={(e) => setDoValidateCompleteness(e.target.checked)}
                >
                    Validate completeness (do_validate_completeness)
                </Checkbox>
                <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                    Проверить наличие: ru.json + lang.json + lang.wav
                </div>
            </div>
        </Modal>

        {/* Download Links Drawer */}
        <Drawer
            title="Скачать результаты"
            placement="right"
            width={700}
            open={downloadDrawerVisible}
            onClose={() => setDownloadDrawerVisible(false)}
        >
            {/* Filters */}
            <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 8 }}>
                {/* Language Filter */}
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Фильтр по языку:</div>
                    <Select
                        value={downloadLangFilter}
                        onChange={handleDownloadLangFilterChange}
                        style={{ width: 200 }}
                        allowClear
                        placeholder="Все языки"
                        options={[
                            { value: 'en', label: 'Английский' },
                            { value: 'ru', label: 'Русский' },
                            { value: 'es', label: 'Испанский' },
                            { value: 'de', label: 'Немецкий' },
                            { value: 'kz', label: 'Казахский' },
                            { value: 'zh', label: 'Китайский' },
                            { value: 'ar', label: 'Арабский' },
                        ]}
                    />
                    <Button
                        icon={<SyncOutlined />}
                        onClick={() => fetchDownloadLinks(downloadLangFilter)}
                        style={{ marginLeft: 8 }}
                        loading={downloadLinksLoading}
                    >
                        Обновить
                    </Button>
                </div>

                {/* Folder Filter */}
                <div>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Фильтр по папкам:</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Select
                            value={downloadFolderFilter}
                            onChange={(val) => {
                                setDownloadFolderFilter(val);
                                if (val !== null) {
                                    setDownloadFolderRangeFrom(null);
                                    setDownloadFolderRangeTo(null);
                                }
                            }}
                            style={{ width: 150 }}
                            allowClear
                            placeholder="Все папки"
                            options={
                                downloadLinks?.max_folder_num
                                    ? Array.from({ length: downloadLinks.max_folder_num }, (_, i) => ({
                                        value: i + 1,
                                        label: `Папка ${i + 1}`
                                    }))
                                    : []
                            }
                        />
                        <span style={{ color: '#888' }}>или диапазон:</span>
                        <InputNumber
                            min={1}
                            max={downloadLinks?.max_folder_num || 1}
                            value={downloadFolderRangeFrom}
                            onChange={(val) => {
                                setDownloadFolderRangeFrom(val);
                                if (val !== null) setDownloadFolderFilter(null);
                            }}
                            placeholder="От"
                            style={{ width: 80 }}
                        />
                        <span>—</span>
                        <InputNumber
                            min={1}
                            max={downloadLinks?.max_folder_num || 1}
                            value={downloadFolderRangeTo}
                            onChange={(val) => {
                                setDownloadFolderRangeTo(val);
                                if (val !== null) setDownloadFolderFilter(null);
                            }}
                            placeholder="До"
                            style={{ width: 80 }}
                        />
                        <Button
                            size="small"
                            onClick={resetFolderFilters}
                            disabled={downloadFolderFilter === null && downloadFolderRangeFrom === null && downloadFolderRangeTo === null}
                        >
                            Сбросить
                        </Button>
                    </div>
                    {downloadLinks?.max_folder_num && (
                        <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>
                            Всего папок: {downloadLinks.max_folder_num}
                        </div>
                    )}
                </div>
            </div>

            {downloadLinksLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Загрузка ссылок...</div>
                </div>
            ) : downloadLinks ? (
                <div>
                    {/* ============ ОБЩИЕ АРТЕФАКТЫ (ПОЛНАЯ ЛЕКЦИЯ) ============ */}
                    <h3 style={{ borderBottom: '2px solid #1890ff', paddingBottom: 8, marginBottom: 16, color: '#1890ff' }}>
                        📚 Общие артефакты для лекции
                    </h3>

                    {/* Full Video */}
                    {downloadLinks.full_video?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <h4 style={{ marginBottom: 8 }}>
                                <VideoCameraOutlined style={{ marginRight: 8 }} />
                                Полное видео без пауз
                            </h4>
                            <List
                                size="small"
                                dataSource={downloadLinks.full_video}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                            </a>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> {item.file_name}</span>}
                                            description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                        />
                                    </List.Item>
                                )}
                            />
                        </div>
                    )}

                    {/* Full Audio */}
                    {downloadLinks.full_audio?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <h4 style={{ marginBottom: 8 }}>
                                🔊 Полное аудио без пауз
                            </h4>
                            <List
                                size="small"
                                dataSource={downloadLinks.full_audio}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                            </a>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> {item.file_name}</span>}
                                            description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                        />
                                    </List.Item>
                                )}
                            />
                        </div>
                    )}

                    {/* Full Subtitles */}
                    {downloadLinks.full_subs?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <h4 style={{ marginBottom: 8 }}>
                                📝 Полные субтитры без пауз
                            </h4>
                            <List
                                size="small"
                                dataSource={downloadLinks.full_subs}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                            </a>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> {item.file_name}</span>}
                                            description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                        />
                                    </List.Item>
                                )}
                            />
                        </div>
                    )}

                    {/* No general artifacts */}
                    {downloadLinks.full_video?.length === 0 &&
                        downloadLinks.full_audio?.length === 0 &&
                        downloadLinks.full_subs?.length === 0 && (
                            <div style={{ color: '#888', padding: 16, textAlign: 'center', marginBottom: 16 }}>
                                Общие артефакты отсутствуют
                            </div>
                        )}

                    {/* ============ АРТЕФАКТЫ ПО ПАПКАМ - РУССКИЕ (ИСХОДНЫЕ) ============ */}
                    {(downloadLangFilter === null || downloadLangFilter === 'ru') && (
                        <>
                            <h3 style={{ borderBottom: '2px solid #52c41a', paddingBottom: 8, marginBottom: 16, marginTop: 24, color: '#52c41a' }}>
                                🇷🇺 Русские артефакты — исходные (по папкам)
                            </h3>

                            {/* Russian Video */}
                            {filterByFolder(downloadLinks.rus_video)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        <VideoCameraOutlined style={{ marginRight: 8 }} />
                                        Русское видео
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.rus_video)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="green">Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Russian Audio */}
                            {filterByFolder(downloadLinks.rus_audio)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        🔊 Русская аудио дорожка
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.rus_audio)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="green">Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Russian Subtitles */}
                            {filterByFolder(downloadLinks.rus_subs)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        📝 Русские субтитры
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.rus_subs)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="green">Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* No Russian artifacts */}
                            {filterByFolder(downloadLinks.rus_video)?.length === 0 &&
                                filterByFolder(downloadLinks.rus_audio)?.length === 0 &&
                                filterByFolder(downloadLinks.rus_subs)?.length === 0 && (
                                    <div style={{ color: '#888', padding: 16, textAlign: 'center' }}>
                                        {(downloadFolderFilter !== null || downloadFolderRangeFrom !== null)
                                            ? 'Русские артефакты для выбранных папок отсутствуют'
                                            : 'Русские артефакты отсутствуют'}
                                    </div>
                                )}
                        </>
                    )}

                    {/* ============ АРТЕФАКТЫ ПО ПАПКАМ - ПЕРЕВЕДЕННЫЕ ЯЗЫКИ ============ */}
                    {(downloadLangFilter === null || ['en', 'es', 'de', 'kz', 'zh', 'ar'].includes(downloadLangFilter)) && (
                        <>
                            <h3 style={{ borderBottom: '2px solid #1890ff', paddingBottom: 8, marginBottom: 16, marginTop: 24, color: '#1890ff' }}>
                                🌐 Переведенные артефакты (по папкам)
                            </h3>

                            {/* Translated Video */}
                            {filterByFolder(downloadLinks.translated_video)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        <VideoCameraOutlined style={{ marginRight: 8 }} />
                                        Видео с аудио дорожкой
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.translated_video)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> <Tag>Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Translated Video No Pauses */}
                            {filterByFolder(downloadLinks.translated_video_no_pauses)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        <ScissorOutlined style={{ marginRight: 8 }} />
                                        Видео без пауз
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.translated_video_no_pauses)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> <Tag>Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Translated Audio */}
                            {filterByFolder(downloadLinks.translated_audio)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        🔊 Аудио дорожка
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.translated_audio)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> <Tag>Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Translated Audio No Pauses */}
                            {filterByFolder(downloadLinks.translated_audio_no_pauses)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        🔊 Аудио без пауз
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.translated_audio_no_pauses)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> <Tag>Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Translated Subtitles */}
                            {filterByFolder(downloadLinks.translated_subs)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        📝 Проверенные субтитры
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.translated_subs)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> <Tag>Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Translated Subtitles No Pauses */}
                            {filterByFolder(downloadLinks.translated_subs_no_pauses)?.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <h4 style={{ marginBottom: 8 }}>
                                        📝 Субтитры без пауз
                                    </h4>
                                    <List
                                        size="small"
                                        dataSource={filterByFolder(downloadLinks.translated_subs_no_pauses)}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    <a href={`https://translation-manag-server.dev.torsunov.pro${item.download_url}`} target="_blank" rel="noopener noreferrer">
                                                        <Button size="small" type="primary" icon={<CloudDownloadOutlined />}>Скачать</Button>
                                                    </a>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<span><Tag color="blue">{item.language?.toUpperCase()}</Tag> <Tag>Папка {item.folder_num}</Tag> {item.file_name}</span>}
                                                    description={<span style={{ color: '#888' }}>{formatFileSize(item.size)}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* No Translated artifacts */}
                            {filterByFolder(downloadLinks.translated_video)?.length === 0 &&
                                filterByFolder(downloadLinks.translated_video_no_pauses)?.length === 0 &&
                                filterByFolder(downloadLinks.translated_audio)?.length === 0 &&
                                filterByFolder(downloadLinks.translated_audio_no_pauses)?.length === 0 &&
                                filterByFolder(downloadLinks.translated_subs)?.length === 0 &&
                                filterByFolder(downloadLinks.translated_subs_no_pauses)?.length === 0 && (
                                    <div style={{ color: '#888', padding: 16, textAlign: 'center' }}>
                                        {(downloadFolderFilter !== null || downloadFolderRangeFrom !== null)
                                            ? 'Переведенные артефакты для выбранных папок отсутствуют'
                                            : 'Переведенные артефакты отсутствуют'}
                                    </div>
                                )}
                        </>
                    )}

                    {/* Summary - no files at all */}
                    {downloadLinks.full_video?.length === 0 &&
                        downloadLinks.full_audio?.length === 0 &&
                        downloadLinks.full_subs?.length === 0 &&
                        downloadLinks.rus_video?.length === 0 &&
                        downloadLinks.rus_audio?.length === 0 &&
                        downloadLinks.rus_subs?.length === 0 &&
                        downloadLinks.translated_video?.length === 0 &&
                        downloadLinks.translated_video_no_pauses?.length === 0 &&
                        downloadLinks.translated_audio?.length === 0 &&
                        downloadLinks.translated_audio_no_pauses?.length === 0 &&
                        downloadLinks.translated_subs?.length === 0 &&
                        downloadLinks.translated_subs_no_pauses?.length === 0 && (
                            <Alert
                                message="Файлы для скачивания не найдены"
                                description="Сначала запустите задачи обработки видео для генерации файлов."
                                type="info"
                                showIcon
                            />
                        )}
                </div>
            ) : (
                <div style={{ color: '#888', textAlign: 'center', padding: 40 }}>
                    Нажмите Обновить для загрузки ссылок
                </div>
            )}
        </Drawer>

        {/* Folder Last Modified Drawer */}
        <Drawer
            title="Даты последних изменений файлов в папках"
            placement="right"
            width={950}
            open={folderModifiedDrawerVisible}
            onClose={() => setFolderModifiedDrawerVisible(false)}
        >
            {/* Filters */}
            <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 8 }}>
                {/* Row 1: Language, Sort, Refresh */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Язык:</div>
                        <Select
                            value={folderModifiedLangFilter}
                            onChange={setFolderModifiedLangFilter}
                            style={{ width: 120 }}
                            allowClear
                            placeholder="Все"
                            options={folderModifiedData?.summary?.languages_found?.map(lang => ({
                                value: lang,
                                label: lang.toUpperCase()
                            })) || []}
                        />
                    </div>
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Сортировка:</div>
                        <Select
                            value={folderModifiedSortBy}
                            onChange={setFolderModifiedSortBy}
                            style={{ width: 200 }}
                            options={[
                                { value: 'folder_asc', label: 'Папка (по возрастанию)' },
                                { value: 'folder_desc', label: 'Папка (по убыванию)' },
                                { value: 'json_date_desc', label: 'JSON дата (новые)' },
                                { value: 'json_date_asc', label: 'JSON дата (старые)' },
                                { value: 'wav_date_desc', label: 'WAV дата (новые)' },
                                { value: 'wav_date_asc', label: 'WAV дата (старые)' },
                            ]}
                        />
                    </div>
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Тип файлов:</div>
                        <Select
                            value={folderModifiedFileTypeFilter}
                            onChange={setFolderModifiedFileTypeFilter}
                            style={{ width: 150 }}
                            options={[
                                { value: 'all', label: 'Все' },
                                { value: 'has_wav', label: 'Есть WAV' },
                                { value: 'no_wav', label: 'Нет WAV' },
                                { value: 'has_json', label: 'Есть JSON' },
                                { value: 'no_json', label: 'Нет JSON' },
                            ]}
                        />
                    </div>
                    <Button
                        icon={<SyncOutlined />}
                        onClick={fetchFolderLastModified}
                        loading={folderModifiedLoading}
                    >
                        Обновить
                    </Button>
                </div>

                {/* Row 2: Folder Range */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: 12 }}>Диапазон папок:</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <InputNumber
                                min={1}
                                max={folderModifiedData?.summary?.total_folders || 1}
                                value={folderModifiedRangeFrom}
                                onChange={setFolderModifiedRangeFrom}
                                placeholder="От"
                                style={{ width: 80 }}
                            />
                            <span>—</span>
                            <InputNumber
                                min={1}
                                max={folderModifiedData?.summary?.total_folders || 1}
                                value={folderModifiedRangeTo}
                                onChange={setFolderModifiedRangeTo}
                                placeholder="До"
                                style={{ width: 80 }}
                            />
                        </div>
                    </div>
                    <Button
                        size="small"
                        onClick={resetFolderModifiedFilters}
                        disabled={
                            folderModifiedLangFilter === null &&
                            folderModifiedSortBy === 'folder_asc' &&
                            folderModifiedRangeFrom === null &&
                            folderModifiedRangeTo === null &&
                            folderModifiedFileTypeFilter === 'all'
                        }
                    >
                        Сбросить фильтры
                    </Button>
                </div>

                {/* Summary */}
                {folderModifiedData?.summary && (
                    <div style={{ marginTop: 12, color: '#666', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Tag color="blue">
                            Всего папок: {folderModifiedData.summary.total_folders}
                        </Tag>
                        <Tag color="green">
                            Показано: {getProcessedFolderData().length}
                        </Tag>
                        <span>
                            Языки: {folderModifiedData.summary.languages_found?.join(', ')}
                        </span>
                    </div>
                )}
            </div>

            {folderModifiedLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Сканирование папок...</div>
                </div>
            ) : folderModifiedData?.folders && getProcessedFolderData().length > 0 ? (
                <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 350px)' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13
                    }}>
                        <thead>
                            <tr style={{ background: '#fafafa' }}>
                                <th style={{
                                    padding: '12px 8px',
                                    textAlign: 'left',
                                    borderBottom: '2px solid #e8e8e8',
                                    position: 'sticky',
                                    top: 0,
                                    background: '#fafafa',
                                    fontWeight: 600,
                                    zIndex: 1
                                }}>
                                    Папка
                                </th>
                                <th style={{
                                    padding: '12px 8px',
                                    textAlign: 'left',
                                    borderBottom: '2px solid #e8e8e8',
                                    position: 'sticky',
                                    top: 0,
                                    background: '#fafafa',
                                    fontWeight: 600,
                                    zIndex: 1
                                }}>
                                    Язык
                                </th>
                                <th style={{
                                    padding: '12px 8px',
                                    textAlign: 'center',
                                    borderBottom: '2px solid #e8e8e8',
                                    position: 'sticky',
                                    top: 0,
                                    background: '#fafafa',
                                    fontWeight: 600,
                                    zIndex: 1
                                }}>
                                    JSON
                                </th>
                                <th style={{
                                    padding: '12px 8px',
                                    textAlign: 'left',
                                    borderBottom: '2px solid #e8e8e8',
                                    position: 'sticky',
                                    top: 0,
                                    background: '#fafafa',
                                    fontWeight: 600,
                                    zIndex: 1
                                }}>
                                    JSON последнее изменение
                                </th>
                                <th style={{
                                    padding: '12px 8px',
                                    textAlign: 'center',
                                    borderBottom: '2px solid #e8e8e8',
                                    position: 'sticky',
                                    top: 0,
                                    background: '#fafafa',
                                    fontWeight: 600,
                                    zIndex: 1
                                }}>
                                    WAV
                                </th>
                                <th style={{
                                    padding: '12px 8px',
                                    textAlign: 'left',
                                    borderBottom: '2px solid #e8e8e8',
                                    position: 'sticky',
                                    top: 0,
                                    background: '#fafafa',
                                    fontWeight: 600,
                                    zIndex: 1
                                }}>
                                    WAV последнее изменение
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {getProcessedFolderData()
                                .map(([folderNum, languages]) => {
                                    const filteredLangs = folderModifiedLangFilter
                                        ? Object.entries(languages).filter(([lang]) => lang === folderModifiedLangFilter)
                                        : Object.entries(languages);

                                    return filteredLangs.map(([lang, data], langIndex) => (
                                        <tr
                                            key={`${folderNum}-${lang}`}
                                            style={{
                                                borderBottom: '1px solid #f0f0f0',
                                                background: langIndex % 2 === 0 ? '#fff' : '#fafafa'
                                            }}
                                        >
                                            {langIndex === 0 && (
                                                <td
                                                    rowSpan={filteredLangs.length}
                                                    style={{
                                                        padding: '8px',
                                                        fontWeight: 600,
                                                        verticalAlign: 'top',
                                                        borderRight: '1px solid #f0f0f0'
                                                    }}
                                                >
                                                    <FolderOpenOutlined style={{ marginRight: 6, color: '#faad14' }} />
                                                    synt_chunks{folderNum}
                                                </td>
                                            )}
                                            <td style={{ padding: '8px' }}>
                                                <Tag color="blue">{lang.toUpperCase()}</Tag>
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                {data.json_count !== undefined ? (
                                                    <Tag>{data.json_count}</Tag>
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                {data.json_last_modified ? (
                                                    <span style={{
                                                        fontFamily: 'monospace',
                                                        fontSize: 12,
                                                        color: '#52c41a'
                                                    }}>
                                                        {data.json_last_modified}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                {data.wav_count !== undefined ? (
                                                    <Tag color="green">{data.wav_count}</Tag>
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                {data.wav_last_modified ? (
                                                    <span style={{
                                                        fontFamily: 'monospace',
                                                        fontSize: 12,
                                                        color: '#1890ff'
                                                    }}>
                                                        {data.wav_last_modified}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ));
                                })}
                        </tbody>
                    </table>
                </div>
            ) : folderModifiedData ? (
                <Alert
                    message="Папки не найдены"
                    description="В папке subs нет подпапок synt_chunks* с файлами переводов."
                    type="info"
                    showIcon
                />
            ) : (
                <div style={{ color: '#888', textAlign: 'center', padding: 40 }}>
                    Нажмите Обновить для загрузки данных
                </div>
            )}
        </Drawer>

        {/* Clips (cut-videos) Drawer — reads R2/CDN manifests + live stats (read-only) */}
        <Drawer
            title={`Клипы лекции${clipsData?.count != null ? ` (${clipsData.count})` : ''}`}
            placement="right"
            width={900}
            open={clipsDrawerVisible}
            onClose={() => setClipsDrawerVisible(false)}
        >
            {clipsLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : (clipsData?.clips?.length > 0) ? (
                <List
                    dataSource={clipsData.clips}
                    renderItem={(clip) => {
                        const dur = clip.duration_seconds != null
                            ? `${Math.floor(clip.duration_seconds / 60)}:${String(Math.round(clip.duration_seconds % 60)).padStart(2, '0')}`
                            : '';
                        return (
                            <List.Item
                                key={clip.clip_id}
                                actions={[
                                    <a href={clip.url} target="_blank" rel="noreferrer">Открыть клип</a>
                                ]}
                            >
                                <List.Item.Meta
                                    title={
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <span>{clip.title || clip.clip_id}</span>
                                            {dur && <Tag>{dur}</Tag>}
                                            {clip.verified && <Tag color="green">Проверен</Tag>}
                                            {clip.censored && <Tag color="red">Цензура</Tag>}
                                            {clip.needs_review && <Tag color="orange">На проверке</Tag>}
                                            {clip.en_status && <Tag color="blue">{clip.en_status === 'done' ? 'EN' : `EN: ${clip.en_status}`}</Tag>}
                                            {clip.dubbed && <Tag color="purple">Дубляж</Tag>}
                                            {clip.is_audio_only && <Tag>Аудио</Tag>}
                                        </div>
                                    }
                                    description={
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', color: '#666', fontSize: 12 }}>
                                            <span>👁 {clip.views ?? 0}</span>
                                            <span>♥ {clip.likes ?? 0}</span>
                                            <span>💬 {clip.comments ?? 0}</span>
                                            {clip.confidence && <span>уверенность: {clip.confidence}</span>}
                                            {(clip.categories || []).slice(0, 4).map((c, i) => <Tag key={i} style={{ margin: 0 }}>{c}</Tag>)}
                                        </div>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            ) : (
                <div style={{ color: '#888', textAlign: 'center', padding: 40 }}>
                    У этой лекции пока нет опубликованных клипов
                </div>
            )}
        </Drawer>
    </>
    );
}
